// AdminAuditLog.tsx — Phase 11E rate-change history viewer.
//
// Lists every event from agent_rate_change_events with filter chips
// (all / direct / cascade) and pagination. Cascade rows badged amber.
//
// Phase 2 UI overhaul (2026-05-15): visual surfaces realigned with the
// player/agent arena-tinted glassmorphic look — hard borders → soft
// arena-tinted insetting rings, neutral palette → arena/neon tokens,
// dense table rows → spaced-out hover-highlight rows, filter pills
// reuse admin's violet identity. Data + API calls unchanged.

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';

interface Row {
  id: number; agent_id: number; pillar: string; game_id: string;
  old_rate_pct: string | null; new_rate_pct: string;
  is_cascade: 0 | 1; triggered_by_event_id: number | null;
  changed_by_admin_id: number | null; changed_by_agent_id: number | null;
  reason: string | null; changed_at: string;
  agent_username: string | null;
  admin_username: string | null;
  actor_agent_username: string | null;
}

const PILLAR_LABEL: Record<string, string> = {
  platform_fee: 'Platform fee', win_loss: 'Win / Loss', offer_reward: 'Offer reward',
};
const PAGE = 50;
type Filter = 'all' | 'direct' | 'cascade';

export default function AuditLogPanel() {
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState<Filter>('all');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE));
      params.set('offset', String(page * PAGE));
      if (filter === 'direct') params.set('is_cascade', '0');
      if (filter === 'cascade') params.set('is_cascade', '1');
      const r = await adminApi.get<{ rows: Row[]; total: number }>(
        `/admin/audit/rate-changes?${params}`
      );
      setRows(r.rows); setTotal(r.total);
    } catch (e) { setErr((e as ApiError).message); }
    finally { setLoading(false); }
  }, [filter, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [filter]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE));
  const directCount = rows.filter(r => !r.is_cascade).length;
  const cascadeCount = rows.filter(r => r.is_cascade).length;

  return (
    <div className="space-y-4">
      {/* Phase 7 — one Audit area: hop to the money-ledger twin. */}
      <Link
        to="/settings/admin-ledger"
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold
                   text-arena-600 dark:text-neon-200/70
                   bg-arena-100/70 dark:bg-white/[0.06]
                   ring-1 ring-inset ring-arena-200/60 dark:ring-white/10
                   hover:bg-arena-100 dark:hover:bg-white/10 transition"
      >
        <Icon name="swap_horiz" size={14} />
        <span>Also in Audit: <span className="font-bold">money movements (Wallet ledger)</span></span>
      </Link>
      <div className="space-y-4">
        {err && <Banner tone="error">{err}</Banner>}

        {/* Filter pills + refresh — sticky inside the panel so the
            controls stay visible while paging through long histories. */}
        <motion.div
          initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
          className="sticky top-0 z-10 -mx-2 px-2 sm:-mx-3 sm:px-3 lg:-mx-4 lg:px-4 py-2
                     backdrop-blur-md
                     bg-white/70 dark:bg-arena-900/60
                     flex flex-wrap gap-2 items-center"
        >
          <FilterPill active={filter === 'all'}     onClick={() => setFilter('all')}     icon="filter_list" label="All"     count={total} />
          <FilterPill active={filter === 'direct'}  onClick={() => setFilter('direct')}  icon="touch_app"   label="Direct"  count={filter === 'direct' ? total : directCount} />
          <FilterPill active={filter === 'cascade'} onClick={() => setFilter('cascade')} icon="account_tree" label="Cascade" count={filter === 'cascade' ? total : cascadeCount} tone="amber" />
          <div className="flex-1" />
          <button onClick={load}
                  className="grid place-items-center w-9 h-9 rounded-xl
                             bg-white dark:bg-arena-700/40
                             ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                             text-arena-700 dark:text-neon-200/80
                             hover:bg-arena-50 dark:hover:bg-arena-700/60 transition">
            <Icon name="refresh" size={16} />
          </button>
        </motion.div>

        {loading && rows.length === 0 && (
          <div className="text-center py-12 text-sm text-arena-600 dark:text-neon-200/60">
            <Icon name="progress_activity" size={20} className="animate-spin inline-block mr-2 align-middle" /> Loading…
          </div>
        )}

        {!loading && rows.length === 0 && (
          <EmptyState icon="history" title="No rate-change events" subtitle="Either no rates have ever been edited, or the current filter excludes them all." />
        )}

        {rows.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            className="overflow-x-auto rounded-2xl
                       bg-white dark:bg-arena-700/40
                       ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                       shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                       backdrop-blur"
          >
            <table className="w-full text-[12.5px]">
              <thead className="bg-arena-50/60 dark:bg-arena-800/40
                                text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60
                                border-b border-arena-200/50 dark:border-arena-600/40">
                <tr>
                  <th className="text-left  py-3 px-4">When</th>
                  <th className="text-left  py-3 px-4">Agent</th>
                  <th className="text-left  py-3 px-4">Pillar / Game</th>
                  <th className="text-right py-3 px-4">Old</th>
                  <th className="text-right py-3 px-4">New</th>
                  <th className="text-left  py-3 px-4">By</th>
                  <th className="text-left  py-3 px-4">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-arena-200/40 dark:divide-arena-700/40">
                {rows.map(r => {
                  const oldNum = r.old_rate_pct == null ? null : Number(r.old_rate_pct);
                  const newNum = Number(r.new_rate_pct);
                  const delta = oldNum == null ? 0 : newNum - oldNum;
                  return (
                    <tr key={r.id}
                        className="hover:bg-arena-50/60 dark:hover:bg-white/[0.03] transition">
                      <td className="py-3 px-4 text-arena-600 dark:text-neon-200/60 whitespace-nowrap text-[11.5px]">
                        {new Date(r.changed_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                      </td>
                      <td className="py-3 px-4">
                        <a href={`/agents/${r.agent_id}`}
                           className="font-bold text-arena-900 dark:text-white hover:text-violet-600 dark:hover:text-violet-300">
                          @{r.agent_username || `#${r.agent_id}`}
                        </a>
                        {r.is_cascade ? (
                          <span className="ml-1.5 inline-flex items-center gap-0.5 text-[9.5px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded
                                           bg-amber-100 text-amber-700 ring-1 ring-amber-200
                                           dark:bg-amber-400/15 dark:text-amber-300 dark:ring-amber-400/30">
                            <Icon name="account_tree" size={10} /> cascade
                          </span>
                        ) : null}
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-arena-600 dark:text-neon-200/60">{PILLAR_LABEL[r.pillar] || r.pillar}</span>
                        <span className="mx-1 text-arena-300 dark:text-neon-200/30">·</span>
                        <span className="capitalize text-arena-800 dark:text-neon-200/90 font-medium">{r.game_id}</span>
                      </td>
                      <td className="py-3 px-4 text-right tabular-nums text-arena-600 dark:text-neon-200/60">{oldNum == null ? '—' : `${oldNum}%`}</td>
                      <td className="py-3 px-4 text-right">
                        <span className="tabular-nums font-bold text-arena-900 dark:text-white">{newNum}%</span>
                        {oldNum != null && delta !== 0 && (
                          <span className={`ml-1.5 text-[10px] font-bold ${delta > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                            {delta > 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(2)}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-[11.5px] text-arena-700 dark:text-neon-200/75">
                        {r.admin_username ? <><Icon name="shield_person" size={11} className="inline-block align-middle mr-0.5" />admin @{r.admin_username}</>
                          : r.actor_agent_username ? <><Icon name="person" size={11} className="inline-block align-middle mr-0.5" />agent @{r.actor_agent_username}</>
                          : '—'}
                      </td>
                      <td className="py-3 px-4 text-[11.5px] text-arena-600 dark:text-neon-200/60 max-w-xs truncate" title={r.reason || ''}>
                        {r.reason || <span className="text-arena-300 dark:text-neon-200/30">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </motion.div>
        )}

        {total > PAGE && (
          <div className="flex items-center justify-end gap-2 text-[12px] text-arena-600 dark:text-neon-200/60">
            <button disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}
                    className="grid place-items-center w-9 h-9 rounded-full
                               bg-white dark:bg-arena-700/40
                               ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                               disabled:opacity-40 hover:bg-arena-50 dark:hover:bg-arena-700/60 transition">
              <Icon name="chevron_left" size={16} />
            </button>
            <span className="tabular-nums px-1">Page {page + 1} / {totalPages}</span>
            <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}
                    className="grid place-items-center w-9 h-9 rounded-full
                               bg-white dark:bg-arena-700/40
                               ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                               disabled:opacity-40 hover:bg-arena-50 dark:hover:bg-arena-700/60 transition">
              <Icon name="chevron_right" size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function FilterPill({
  active, onClick, icon, label, count, tone,
}: {
  active: boolean; onClick: () => void; icon: string; label: string; count?: number;
  tone?: 'amber';
}) {
  const activeStyle = tone === 'amber'
    ? 'bg-amber-600 text-white ring-amber-600 shadow-[0_2px_10px_rgba(245,158,11,0.4)]'
    : 'bg-violet-600 text-white ring-violet-600 shadow-[0_2px_10px_rgba(139,92,246,0.4)]';
  return (
    <button onClick={onClick}
            className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold transition ring-1 ring-inset ${
              active
                ? activeStyle
                : 'bg-white text-arena-700 ring-arena-200/60 hover:bg-arena-50 dark:bg-arena-700/40 dark:text-neon-200/80 dark:ring-arena-600/40 dark:hover:bg-arena-700/60'
            }`}>
      <Icon name={icon} size={13} /> {label}
      {typeof count === 'number' && (
        <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded-full tabular-nums ${
          active ? 'bg-white/25' : 'bg-arena-100 text-arena-600 dark:bg-arena-700/60 dark:text-neon-200/70'
        }`}>{count}</span>
      )}
    </button>
  );
}

function Banner({ tone, children }: { tone: 'error' | 'success' | 'info'; children: React.ReactNode }) {
  const m = tone === 'error'
    ? 'bg-rose-50 dark:bg-rose-500/10 ring-rose-200/60 dark:ring-rose-400/30 text-rose-700 dark:text-rose-200'
    : tone === 'success' ? 'bg-emerald-50 dark:bg-emerald-500/10 ring-emerald-200/60 dark:ring-emerald-400/30 text-emerald-700 dark:text-emerald-200'
    : 'bg-arena-50/70 dark:bg-arena-800/60 ring-arena-200/60 dark:ring-arena-600/40 text-arena-800 dark:text-neon-200/80';
  return <div className={`rounded-2xl ring-1 ring-inset px-4 py-3 text-[13px] ${m}`}>{children}</div>;
}

function EmptyState({ icon, title, subtitle }: { icon: string; title: string; subtitle: string }) {
  return (
    <div className="rounded-2xl bg-white dark:bg-arena-700/40
                    ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                    shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                    px-6 py-12 text-center backdrop-blur">
      <div className="mx-auto grid place-items-center w-14 h-14 rounded-2xl
                      bg-arena-100/60 dark:bg-arena-800/60 mb-3">
        <Icon name={icon} size={26} className="text-arena-400 dark:text-neon-200/40" />
      </div>
      <div className="font-bold text-arena-900 dark:text-white">{title}</div>
      <div className="text-[12px] text-arena-600 dark:text-neon-200/60 mt-1">{subtitle}</div>
    </div>
  );
}
